import React, { useState, useEffect } from 'react';
import { Grid, Container } from '@material-ui/core';
import {
  BoxV2 as Box,
  Flex,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
} from 'portal-commons';

import { globalGetService } from '../../../../../utils/globalApiServices';
import { CampaignListingRow } from '../../components';
import { Loader, FilterToggleButton } from '../../../../../shared_elements';
import { enumController } from '@/apis/core';
import { fetchBrandSuggestions, fetchCspSuggestions } from '../../apiServices';
import '../../assets/campaign-listing-module.scss';
import { MNO_STATUS } from '../../../../../constants';
import { MaxCampaignCount } from '../../constants';
import { getApprovedDcas } from '@/applications/MnoPortal/Campaigns/apis';

const FILTER_CONFIG = {
  uid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter Campaign ID',
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  cspName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA Name',
    suggestions: [],
  },
  dcaUid: {
    type: FilterType.Dropdown,
    options: [],
    label: 'DCA Name',
    placeholder: 'DCA Name',
  },
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
    placeholder: 'Use-Case',
  },
  resellerUid: {
    type: FilterType.Text,
    label: 'Reseller ID',
    placeholder: 'Enter Reseller ID',
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
    placeholder: 'TCR Status',
  },
  mnoStatus: {
    type: FilterType.Dropdown,
    options: MNO_STATUS,
    label: 'MNO Status',
    placeholder: 'MNO Status',
    multiple: true,
    width: 250,
  },
};

const headRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'cspName', label: 'CSP DBA NAME', sortable: true },
  { id: 'mnoDcaName', label: 'DCA NAME', sortable: false },
  { id: 'usecase', label: 'USE-CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'resellerUid', label: 'RESELLER ID', sortable: false },
  { id: 'tcrStatus', label: 'TCR STATUS', sortable: false },
  { id: 'operationStatus', label: 'MNO STATUS', sortable: false },
];

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value) => fetchBrandSuggestions({ prefix: value }),
    },
    cspName: {
      minLength: 2,
      load: (value) => fetchCspSuggestions({ prefix: value }),
    },
  },
};

function CampaignListingUS() {
  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });
  const { appliedFilters, candidateFilters, additionalFilters } = filter;
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const fetchCampaigns = async (tableFilter) => {
    const query = {
      ...tableFilter,
      ...appliedFilters,
      ...additionalFilters,
      channelId: '10DLC',
    };

    const isExpired = query.status === 'EXPIRED';
    const includesReview = query.mnoStatus?.includes('REVIEW');
    if (isExpired && includesReview) {
      return {
        data: table.tableData,
        total: table.total,
        rowsPerPage: table.pagination.rowsPerPage,
        page: table.pagination.page,
      };
    }

    if (isExpired && !query.mnoStatus) {
      query.mnoStatus = 'REJECTED,REGISTERED,SUSPENDED';
    }

    const response = await globalGetService('mno/campaigns', query).then(
      (response) => response?.data
    );

    return {
      data: response?.records || [],
      rowsPerPage: response?.recordsPerPage || 10,
      total: response?.totalRecords || 0,
      page: response?.page || 1,
    };
  };

  const table = useTable(fetchCampaigns, {
    cappingTotal: MaxCampaignCount,
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'createDate',
      ascendingOrder: false,
    },
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    processFilterSpecialCase(appliedFilters, filter.handleApply);
  }, [appliedFilters.status, appliedFilters.mnoStatus]);

  useEffect(() => {
    processFilterSpecialCase(candidateFilters, filter.handleEdit);
  }, [candidateFilters.status, candidateFilters.mnoStatus]);

  const fetchFilterOptions = async () => {
    const { setOptions } = filter;
    const usecaseTypes = await enumController.getUsecaseTypes({
      channelId: '10DLC',
    });
    setOptions(
      'usecase',
      usecaseTypes.map((item) => ({
        label: item.displayName,
        value: item.id,
      }))
    );
    const approvedDcas = await getApprovedDcas();
    setOptions(
      'dcaUid',
      approvedDcas.map((item) => ({
        label: item.displayName,
        value: item.uid,
      }))
    );
  };

  const updateMnoStatusReviewOptionDisabled = (disabled = false) => {
    const { setOptions } = filter;
    setOptions(
      'mnoStatus',
      MNO_STATUS.map((item) =>
        item.value === 'REVIEW' ? { ...item, disabled } : item
      )
    );
  };

  const processFilterSpecialCase = (filters, callback) => {
    const { status } = filters;
    const isExpired = status === 'EXPIRED';
    updateMnoStatusReviewOptionDisabled(isExpired);
    if (isExpired) {
      // remove review from mnoStatus
      const mnoStatus = filters.mnoStatus || '';
      const mnoStatusArr = mnoStatus.split(',').filter(Boolean);
      const hasReviewInMnoStatus = mnoStatusArr.includes('REVIEW');
      if (hasReviewInMnoStatus) {
        const mnoStatusWithoutReview = mnoStatusArr
          .filter((item) => item !== 'REVIEW')
          .join(',');
        if (mnoStatusWithoutReview) {
          callback({ ...filters, mnoStatus: mnoStatusWithoutReview });
        } else {
          callback({ ...filters, mnoStatus: undefined });
        }
      }
    }
  };

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  const displayTitle = (total, appliedFilter) => {
    if (table.pageLoading) return '10DLC Campaigns';
    if (!appliedFilter?.mnoStatus && !!appliedFilter?.status) {
      return `${total} ${
        appliedFilter.status === 'EXPIRED' ? 'Deactivated' : 'Active'
      } 10DLC Campaigns`;
    }
    return `${total} 10DLC Campaigns`;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <section className="campaigns-listing-section page-content">
        {table.pageLoading ? (
          <Loader />
        ) : (
          <Container maxWidth={false} className="campaigns-listing-container">
            <Box>
              <div className="title-block">
                <h2>
                  {table.total === 0
                    ? '10DLC Campaigns'
                    : `${displayTitle(table.total, appliedFilters)}`}
                </h2>
                <span>
                  Results capped at first{' '}
                  {MaxCampaignCount.toLocaleString('en-US')} records
                </span>
              </div>
            </Box>
            <Flex
              sx={{
                flexDirection: 'column',
                marginTop: 's',
                marginBottom: 's',
              }}
            >
              <Box>
                <FilterToggleButton
                  visible={isFilterVisible}
                  onClick={toggleFilterVisibility}
                />
              </Box>
              {isFilterVisible && (
                <TableFilter
                  portal="mno"
                  configs={filter.configs}
                  candidateValues={candidateFilters}
                  appliedValues={appliedFilters}
                  onCandidateValuesChange={filter.handleEdit}
                  onAppliedValuesChange={filter.handleApply}
                  className="filters"
                  data-testid="campaigns"
                />
              )}
            </Flex>
            <Grid container justifyContent="center" spacing={0}>
              <Table
                testId="campaignsTable"
                loading={table.loading}
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                emptyState="no campaigns to view"
                filter={table.filter}
                headRows={headRows}
                data={table.tableData}
                rowKey="uid"
                renderRow={(data) => (
                  <CampaignListingRow key={data.uid} campaign={data} />
                )}
                pagination={table.pagination}
              />
            </Grid>
          </Container>
        )}
      </section>
    </Box>
  );
}
export default CampaignListingUS;
